/**
 * WFA Configuration Mock
 *
 * Mirrors the Walk-Forward Analysis configuration panel from the
 * platform — tabbed UI with ratio-based settings and validation.
 */

const inputStyle: React.CSSProperties = {
  background: '#0c1520',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '0.25rem',
  padding: '0.25rem 0.5rem',
  fontSize: '0.6875rem',
  color: '#e5e7eb',
  fontFamily: 'ui-monospace, monospace',
  width: 60,
  textAlign: 'right',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.625rem',
  color: '#9ca3af',
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  fontSize: '0.5625rem',
  fontWeight: active ? 700 : 400,
  color: active ? '#e5e7eb' : '#6b7280',
  padding: '0.25rem 0.625rem',
  background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
  border: active ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
  borderRadius: '0.25rem',
  cursor: 'default',
});

export default function WFAConfigMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Walk-Forward Configuration
      </div>
      <div className="platform-mock-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.75rem' }}>
          <span style={tabStyle(true)}>Basic Settings</span>
          <span style={tabStyle(false)}>Advanced Options</span>
        </div>

        {/* Analysis Mode */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={labelStyle}>Analysis Mode</div>
          <div style={{
            ...inputStyle,
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '0.25rem',
          }}>
            <span>Ratio-based Split</span>
            <span style={{ color: '#4b5563', fontSize: '0.5rem' }}>&#9662;</span>
          </div>
        </div>

        {/* Ratio settings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <div style={labelStyle}>Training Ratio</div>
            <div style={{ ...inputStyle, width: '100%', textAlign: 'left', marginTop: '0.25rem' }}>0.70</div>
          </div>
          <div>
            <div style={labelStyle}>Testing Ratio</div>
            <div style={{ ...inputStyle, width: '100%', textAlign: 'left', marginTop: '0.25rem' }}>0.30</div>
          </div>
        </div>

        {/* Visual ratio bar */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ width: '70%', background: 'rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '0.5rem', color: '#93c5fd', fontWeight: 600 }}>70% Training</span>
            </div>
            <div style={{ width: '30%', background: 'rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '0.5rem', color: '#86efac', fontWeight: 600 }}>30% Testing</span>
            </div>
          </div>
        </div>

        {/* Summary info */}
        <div style={{
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: '0.375rem',
          padding: '0.5rem 0.625rem',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.5625rem',
        }}>
          <div>
            <div style={{ color: '#6b7280' }}>Target Trades/Window</div>
            <div style={{ color: '#e5e7eb', fontWeight: 600 }}>30+</div>
          </div>
          <div>
            <div style={{ color: '#6b7280' }}>Est. Windows</div>
            <div style={{ color: '#e5e7eb', fontWeight: 600 }}>8</div>
          </div>
          <div>
            <div style={{ color: '#6b7280' }}>Window Ratio</div>
            <div style={{ color: '#e5e7eb', fontWeight: 600 }}>3:1</div>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        Set the training/testing split and window parameters before running walk-forward analysis.
      </div>
    </div>
  );
}
